<script setup lang="ts">
import type { AxiosError } from 'axios'
import type { BoardItemDetail } from '@store'
import { ROUTES } from '@configs'

definePageMeta({
  layout: 'one-column',
})

useScrollRestorationNested()

const etcStore = useEtcStore()
const { locale } = useI18n()
const route = useRoute()
const event = ref<BoardItemDetail>()
const err = ref<AxiosError>()
const { gtEvent } = useGoogleTag()
const { t } = useI18n()

watch(
  () => locale.value,
  () => {
    setTimeout(async () => {
      await etcStore.fetchBoardList(etcStore.eventList.payload, true)
      event.value = await etcStore.fetchBoardDetail({
        nttGroupId: String(route.params.id),
        type: 'event',
      })
    })
  }
)

const { data: _event } = await useAsyncData('event', async () => {
  try {
    const result = await etcStore.fetchBoardDetail({
      nttGroupId: String(route.params.id),
      type: 'event',
    })
    return Promise.resolve(result)
  } catch (_err) {
    err.value = _err as AxiosError
    return Promise.reject(err)
  }
})

if (_event.value) {
  useSetSEO({
    useHeadArgs: [
      {
        title: _event.value.nttSj,
        meta: [
          {
            name: 'description',
            content: useGetMaxThreeParagraphTexts(_event.value.nttCn),
          },
        ],
      },
    ],
    useSeoMetaArgs: [
      {
        ogTitle: _event.value.nttSj,
        ogDescription: useGetMaxThreeParagraphTexts(_event.value.nttCn),
        ogImage: useGetFirstImageSource(_event.value.nttCn),
      },
    ],
  })

  event.value = _event.value
}

if (err.value) {
  throw createError({
    statusCode: 404,
    statusMessage: `event ${route.params.id} is not find`,
  })
}

const onGoEventList = () => {
  gtEvent('clickEvent', {
    eventCategory: '클릭',
    eventAction: '이벤트 상세 > 목록',
    eventLabel: t('board.label.list'),
    eventSlot: '',
    eventI18nAddr: useKoreanTranslation('board.label.list'),
    eventComponent: 'Button',
  })
  useNavigations({ url: ROUTES.BOARD_EVENT.path, query: route.query })
}
</script>

<template>
  <div class="board-wrapper">
    <NovaBoxBoardSideMenu :active="'event'" />

    <NovaBoxBoardDetail
      :board-title="$t('board.title.event')"
      :board-detail="event!"
    >
      <template #actions>
        <NovaButtonText
          :label="$t('board.label.list')"
          :theme="'primary-blue'"
          :width="100"
          class="btn-action"
          @click="onGoEventList"
        />
      </template>

      <template #links>
        <NovaBoxBoardDetailLink
          v-if="event?.prev"
          :direction="'prev'"
          :board-item="event!.prev!"
          :link-path="useRoutePathIdChange(ROUTES.BOARD_EVENT_DETAIL.path, { id: event!.prev!.nttGroupId })"
          :ga-slot="$t('board.title.event')"
        />

        <NovaBoxBoardDetailLink
          v-if="event?.next"
          :direction="'next'"
          :board-item="event!.next!"
          :link-path="useRoutePathIdChange(ROUTES.BOARD_EVENT_DETAIL.path, { id: event!.next!.nttGroupId })"
          :ga-slot="$t('board.title.event')"
        />
      </template>
    </NovaBoxBoardDetail>
  </div>
</template>

<style lang="scss" scoped>
.board-wrapper {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 40px;
  padding-top: 20px;
  color: $color-text-2;

  @include mobile {
    padding: 15px 20px;
    flex-direction: column;
    gap: 20px;
  }
}
</style>
